import { useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '@design-system';
import { cn } from '@client/lib';
import { formatCurrency } from '@shared/formatCurrency';

export interface FundingProgressBarProps {
  allocatedAmount: number;
  totalCost: number;
}

export function FundingProgressBar({ allocatedAmount, totalCost }: FundingProgressBarProps) {
  const progressPercentage = totalCost > 0 ? Math.min(100, (allocatedAmount / totalCost) * 100) : 0;
  const remainingAmount = Math.max(0, totalCost - allocatedAmount);
  const isFullyFunded = allocatedAmount >= totalCost;

  // Track previous funded state to detect transition to fully funded
  const wasFullyFundedRef = useRef(isFullyFunded);

  useEffect(() => {
    // Show toast when transitioning from not funded to fully funded
    if (isFullyFunded && !wasFullyFundedRef.current) {
      toast.success('Policy is fully funded!', {
        description: 'You can continue to the next step.',
      });
    }
    wasFullyFundedRef.current = isFullyFunded;
  }, [isFullyFunded]);

  const progressIndicatorClass = isFullyFunded
    ? '[&_[data-slot="progress-indicator"]]:bg-green-700'
    : '[&_[data-slot="progress-indicator"]]:bg-primary';

  return (
    <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-900">Funding Progress</p>
        {isFullyFunded ? (
          <div className="flex items-center gap-1.5">
            <span className="text-green-7 00 text-sm font-medium">{formatCurrency(totalCost)}</span>
            <CheckCircle2 className="size-4 text-green-700" />
          </div>
        ) : (
          <p className="text-sm font-medium text-gray-900">
            {formatCurrency(allocatedAmount)} / {formatCurrency(totalCost)}
          </p>
        )}
      </div>

      {/* Progress Bar */}
      <Progress
        value={progressPercentage}
        className={cn('h-2.5 bg-gray-100', progressIndicatorClass)}
      />

      {/* Status Message - only show when not fully funded */}
      {!isFullyFunded && (
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <AlertCircle className="size-4 shrink-0" />
          <p>
            {formatCurrency(remainingAmount)} remaining — select another account or increase
            allocation
          </p>
        </div>
      )}
    </div>
  );
}
